import { z } from 'zod';

import {
  currencySettingsSchema,
  GATEWAY_VARIANTS,
  ROUTE_VARIANTS,
  type RouteKey,
} from './legacy-route-responses.js';

// CON-328: defined beside NonprofitSettingsWrittenAttributes (the echo), which this file already
// imports from; CON-350 imports the schema from here.
export {
  CURRENCY_SETTINGS_OFFERED_CODES,
  currencySettingsSchema,
  type CurrencySettings,
} from './legacy-route-responses.js';

const ORG_AVAILABILITY_ROUTE = 'POST /onboarding/org-availability' as const;
export type AccountsRouteKey = RouteKey | typeof ORG_AVAILABILITY_ROUTE;

const emptyInputSchema = z.object({}).strict();
const jsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);
const flatErrorInputSchema = z.object({}).strict();
const permissionsSchema = z.record(z.string(), z.array(z.string()));
const onboardingSelectedPlanInputSchema = z
  .string()
  .transform((value) => value.toUpperCase())
  .pipe(z.literal('STARTER'))
  .nullable();
const verifyScriptDetectedAtSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?\+00:00$/);
const verifyScriptFetchBodySchema = z.discriminatedUnion('installed', [
  z
    .object({
      installed: z.literal(true),
      method: z.literal('fetch'),
      detectedAt: verifyScriptDetectedAtSchema,
    })
    .strict(),
  z.object({ installed: z.literal(false), method: z.literal('fetch') }).strict(),
]);

const standardNonprofitSettingsInputSchema = z
  .object({
    addressLine1: z.string(),
    addressLine2: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    ein: z.string(),
    organizationBaseURLs: z.array(z.string()).optional(),
    timezone: z.string().optional(),
    brandColor: z.string().optional(),
    orgDescription: z.string().optional(),
    websiteUrl: z.string().optional(),
    privacyPolicyLink: z.string().optional(),
    registrationContactUserId: z.string().optional(),
    registrationContactEmail: z.string().optional(),
    registrationContactPhone: z.string().optional(),
    registrationContactFirstName: z.string().optional(),
    registrationContactLastName: z.string().optional(),
    currencySettings: currencySettingsSchema.optional(),
  })
  .strict();
const logoVersionSchema = z.number().int().positive();
const nonprofitSettingsInputSchema = z.union([
  z
    .object({
      requestType: z.literal('LOGO_VERSION'),
      merchantName: z.string().min(1),
      logoVersion: logoVersionSchema,
    })
    .strict(),
  standardNonprofitSettingsInputSchema,
]);
const logoVersionOutputSchema = z.object({ logoVersion: logoVersionSchema }).strict();

const organizationAddressSchema = z
  .object({
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
  })
  .strict();

const createOrganizationInputSchema = z
  .object({
    orgName: z.string(),
    organizationSlug: z.string().optional(),
    entityType: z.string().optional(),
    ein: z.string().optional(),
    legalName: z.string().optional(),
    email: z.string().optional(),
    phoneNumber: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    description: z.string().optional(),
    websiteUrl: z.string().optional(),
    hasWebsite: z.boolean().optional(),
    isTaxExempt: z.boolean().optional(),
    causeCategory: z.string().optional(),
    timezone: z.string().optional(),
    logo: z.string().optional(),
    brandColor: z.string().optional(),
    selectedPlan: z.string().optional(),
    address: organizationAddressSchema.optional(),
    // Validation, whitespace fallback, and canonicalization belong to the §6.28–§6.29 domain port.
    idempotencyKey: z.string().optional(),
  })
  .strict();

const organizationAvailabilityInputSchema = z
  .object({ orgName: z.string(), organizationSlug: z.string() })
  .strict();
const organizationAvailabilityOutputSchema = z
  .object({
    nameTaken: z.boolean(),
    slugTaken: z.boolean(),
    slugError: z.string().nullable(),
  })
  .strict();

const onboardingStateInputSchema = z
  .object({
    onboardingCurrentStep: z.string().optional(),
    driverFlowsCompleted: z.record(z.boolean()).optional(),
    onboardingCompletedSteps: z.array(z.string()).optional(),
    checklistMinimized: z.boolean().optional(),
    scriptInstalled: z.boolean().optional(),
    hasWebsite: z.boolean().optional(),
    lastPublishedFormId: z.string().optional(),
    logoVersion: logoVersionSchema.optional(),
    onboardingSelectedPlan: onboardingSelectedPlanInputSchema.optional(),
  })
  .strict();

const rawWebhookInputSchema = z.object({ rawBody: z.string(), signature: z.string() }).strict();
const clerkWebhookInputSchema = z
  .object({
    rawBody: z.string(),
    svixId: z.string(),
    svixTimestamp: z.string(),
    svixSignature: z.string(),
  })
  .strict();

/**
 * Strict tRPC inputs for every one of the legacy API's 46 method/path routes.
 * Transport-only values (query/path/header values) are folded into the procedure
 * input object; webhook payloads remain raw so signature verification can precede
 * JSON parsing. Unknown legacy values are explicit JSON values, never `any`.
 */
export const accountsRouteInputSchemas = {
  'DELETE /org-contact-tags': emptyInputSchema,
  'PATCH /org-custom-fields': z.object({ key: z.string(), label: z.string() }).strict(),
  'POST /privacy-policy/generate': emptyInputSchema,
  'POST /privacy-policy/accept': z.object({ content: z.string() }).strict(),
  'POST /users': z
    .object({
      email: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      avatarUrl: z.string().optional(),
      phone: z.string().optional(),
      preferences: z.record(jsonValueSchema).optional(),
    })
    .strict(),
  'PATCH /custom-roles': z
    .object({
      slug: z.string(),
      label: z.string(),
    })
    .strict(),
  'POST /onboarding/create-org': createOrganizationInputSchema,
  [ORG_AVAILABILITY_ROUTE]: organizationAvailabilityInputSchema,
  'POST /subscription-cancel': emptyInputSchema,
  'GET /users/me': emptyInputSchema,
  'POST /webhooks/stripe-subscription': rawWebhookInputSchema,
  'GET /stripe/account-status': z.object({ live: z.boolean().optional() }).strict(),
  'POST /privacy-policy/overlay': z.object({ enabled: z.boolean() }).strict(),
  'PATCH /nonprofit-settings': nonprofitSettingsInputSchema,
  'GET /clerk-users': z.object({ withPhones: z.boolean().optional() }).strict(),
  'DELETE /clerk-users': z
    .object({ invitationId: z.string().optional(), userId: z.string().optional() })
    .strict()
    .refine((input) => input.invitationId !== undefined || input.userId !== undefined, {
      message: 'invitationId or userId is required',
    }),
  'PATCH /clerk-users': z
    .object({
      userId: z.string(),
      charitystackRole: z.string().optional(),
      permissionOverrides: jsonValueSchema.optional(),
    })
    .strict(),
  'POST /website-enrich': z.object({ url: z.string() }).strict(),
  'DELETE /org-role-permissions': emptyInputSchema,
  'PUT /org-contact-tags': z.object({ tags: z.array(z.string()) }).strict(),
  'GET /org-custom-fields': emptyInputSchema,
  'PUT /org-slug': z.object({ slug: z.string() }).strict(),
  'POST /verify-script': z.object({ websiteUrl: z.string() }).strict(),
  'POST /custom-roles': z
    .object({ slug: z.string(), label: z.string(), permissions: z.array(z.string()).optional() })
    .strict(),
  'GET /merchant': emptyInputSchema,
  'GET /stripe/account-email': emptyInputSchema,
  'POST /stripe/management-session': z
    .object({ expectedStripeAccountId: z.string().optional() })
    .strict(),
  'DELETE /custom-roles': z
    .object({ slug: z.string(), reassignRole: z.string().optional() })
    .strict(),
  'POST /stripe/account-session': emptyInputSchema,
  'GET /users': z
    .object({ orgId: z.string(), limit: z.number().int().positive().optional() })
    .strict(),
  'POST /clerk-users': z
    .object({
      email: z.string(),
      charitystackRole: z.string().optional(),
      permissionOverrides: jsonValueSchema.optional(),
    })
    .strict(),
  'GET /public/privacy-policy/{slug}': z.object({ slug: z.string() }).strict(),
  'POST /ein-lookup': z
    .object({
      query: z.string().optional(),
      orgName: z.string().optional(),
      ein: z.string().optional(),
    })
    .strict()
    .refine(
      (input) =>
        input.query !== undefined || input.orgName !== undefined || input.ein !== undefined,
      {
        message: 'query, orgName, or ein is required',
      },
    ),
  'PATCH /users/me': z
    .object({ preferences: z.record(jsonValueSchema).optional(), phone: z.string().optional() })
    .strict()
    .refine((input) => input.preferences !== undefined || input.phone !== undefined, {
      message: 'preferences or phone is required',
    }),
  'GET /org-role-permissions': emptyInputSchema,
  'GET /get-location': emptyInputSchema,
  'POST /webhooks/clerk': clerkWebhookInputSchema,
  'PATCH /onboarding/state': onboardingStateInputSchema,
  'POST /users/me/active-org/sync': emptyInputSchema,
  'PATCH /global-custom-inputs': z.object({ customInputs: jsonValueSchema }).strict(),
  'PATCH /billing-plan': z.object({ plan: z.string() }).strict(),
  'PATCH /user-profile': z
    .object({ phone_number: z.string().optional(), address: jsonValueSchema.optional() })
    .strict()
    .refine((input) => input.phone_number !== undefined || input.address !== undefined, {
      message: 'phone_number or address is required',
    }),
  // Legacy defaults an omitted map to {}, which intentionally wipes every customization.
  'PUT /org-role-permissions': z.object({ rolePermissions: permissionsSchema.optional() }).strict(),
  'POST /webhooks/stripe': rawWebhookInputSchema,
  'GET /org-contact-tags': flatErrorInputSchema,
  'DELETE /org-custom-fields': z.object({ key: z.string() }).strict(),
  'POST /org-custom-fields': z
    .object({ label: z.string(), type: z.literal('string').optional() })
    .strict(),
} as const satisfies Record<AccountsRouteKey, z.ZodTypeAny>;

type LegacyVariant = z.ZodObject<
  {
    statusCode: z.ZodTypeAny;
    headers: z.ZodTypeAny;
    body: z.ZodTypeAny;
  },
  'strict'
>;

type VariantBodySchema<V> =
  V extends z.ZodObject<infer Shape>
    ? Shape extends { body: infer Body extends z.ZodTypeAny }
      ? Body
      : never
    : never;

type HandlerVariantKey<V, Excluded extends PropertyKey> = {
  [K in keyof V]: K extends string
    ? K extends `GATEWAY_${string}` | `${string}OPTIONS${string}` | `${string}PREFLIGHT${string}`
      ? never
      : K extends Excluded
        ? never
        : K
    : never;
}[keyof V];

type DecodedBodyOutput<
  V extends Record<string, z.ZodTypeAny>,
  Excluded extends keyof V = never,
> = z.output<VariantBodySchema<V[HandlerVariantKey<V, Excluded>]>>;

const gatewayDecodedBodySchema = z.union(
  Object.values(GATEWAY_VARIANTS).map((schema) => (schema as LegacyVariant).shape.body) as [
    z.ZodTypeAny,
    z.ZodTypeAny,
    ...z.ZodTypeAny[],
  ],
);

function decodedBodySchema<
  const V extends Record<string, z.ZodTypeAny>,
  const Excluded extends keyof V = never,
>(variants: V, excluded: readonly Excluded[] = []): z.ZodType<DecodedBodyOutput<V, Excluded>> {
  const schemas = Object.entries(variants)
    .filter(
      ([name]) =>
        !name.startsWith('GATEWAY_') &&
        !name.includes('OPTIONS') &&
        !name.includes('PREFLIGHT') &&
        !excluded.includes(name as Excluded),
    )
    .map(([, schema]) => (schema as LegacyVariant).shape.body);

  if (schemas.length === 0) throw new Error('route has no handler response variants');
  const handlerBodySchema =
    schemas.length === 1
      ? schemas[0]!
      : z.union(schemas as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]]);

  return handlerBodySchema.refine(
    (body) => !gatewayDecodedBodySchema.safeParse(body).success,
    'API Gateway transport failures are not procedure outputs',
  ) as z.ZodType<DecodedBodyOutput<V, Excluded>>;
}

type AccountsRouteOutputSchemaMap = {
  [R in RouteKey]: R extends 'POST /verify-script'
    ? z.ZodType<
        | DecodedBodyOutput<(typeof ROUTE_VARIANTS)[R], 'VERIFY_SCRIPT_FETCH_200'>
        | z.output<typeof verifyScriptFetchBodySchema>
      >
    : z.ZodType<DecodedBodyOutput<(typeof ROUTE_VARIANTS)[R]>>;
};

/** Exact decoded-body outputs, mechanically projected from the reviewed legacy oracle. */
const legacyRouteOutputSchemas = (Object.keys(ROUTE_VARIANTS) as RouteKey[]).reduce(
  (schemas, route) => {
    const schema =
      route === 'POST /verify-script'
        ? z.union([
            decodedBodySchema(ROUTE_VARIANTS[route], ['VERIFY_SCRIPT_FETCH_200']),
            verifyScriptFetchBodySchema,
          ])
        : decodedBodySchema(ROUTE_VARIANTS[route]);
    (schemas as Record<RouteKey, z.ZodTypeAny>)[route] = schema;
    return schemas;
  },
  {} as AccountsRouteOutputSchemaMap,
);

export const accountsRouteOutputSchemas = {
  ...legacyRouteOutputSchemas,
  'PATCH /nonprofit-settings': z.union([
    legacyRouteOutputSchemas['PATCH /nonprofit-settings'],
    logoVersionOutputSchema,
  ]),
  [ORG_AVAILABILITY_ROUTE]: organizationAvailabilityOutputSchema,
} as const satisfies Record<AccountsRouteKey, z.ZodTypeAny>;

/** Operation names become the `org.accounts.*` tRPC procedure names in INF-85. */
export const accountsProcedureRoutes = {
  getMerchant: 'GET /merchant',
  getLocation: 'GET /get-location',
  patchNonprofitSettings: 'PATCH /nonprofit-settings',
  patchGlobalCustomInputs: 'PATCH /global-custom-inputs',
  putOrgSlug: 'PUT /org-slug',
  getOrgContactTags: 'GET /org-contact-tags',
  putOrgContactTags: 'PUT /org-contact-tags',
  deleteOrgContactTags: 'DELETE /org-contact-tags',
  getOrgCustomFields: 'GET /org-custom-fields',
  createOrgCustomField: 'POST /org-custom-fields',
  updateOrgCustomField: 'PATCH /org-custom-fields',
  deleteOrgCustomField: 'DELETE /org-custom-fields',
  generatePrivacyPolicy: 'POST /privacy-policy/generate',
  acceptPrivacyPolicy: 'POST /privacy-policy/accept',
  toggleOverlayPrivacyPolicy: 'POST /privacy-policy/overlay',
  getPublicPrivacyPolicy: 'GET /public/privacy-policy/{slug}',
  getOrgRolePermissions: 'GET /org-role-permissions',
  putOrgRolePermissions: 'PUT /org-role-permissions',
  deleteOrgRolePermissions: 'DELETE /org-role-permissions',
  createCustomRole: 'POST /custom-roles',
  updateCustomRole: 'PATCH /custom-roles',
  deleteCustomRole: 'DELETE /custom-roles',
  listClerkUsers: 'GET /clerk-users',
  inviteClerkUser: 'POST /clerk-users',
  updateClerkUser: 'PATCH /clerk-users',
  deleteClerkUser: 'DELETE /clerk-users',
  clerkWebhook: 'POST /webhooks/clerk',
  stripeConnectWebhook: 'POST /webhooks/stripe',
  getStripeAccountStatus: 'GET /stripe/account-status',
  createStripeManagementSession: 'POST /stripe/management-session',
  getStripeAccountEmail: 'GET /stripe/account-email',
  createStripeAccountSession: 'POST /stripe/account-session',
  getCurrentUser: 'GET /users/me',
  patchCurrentUser: 'PATCH /users/me',
  syncActiveOrg: 'POST /users/me/active-org/sync',
  createUser: 'POST /users',
  listUsersByOrg: 'GET /users',
  patchUserProfile: 'PATCH /user-profile',
  einLookup: 'POST /ein-lookup',
  websiteEnrich: 'POST /website-enrich',
  verifyScript: 'POST /verify-script',
  createOrg: 'POST /onboarding/create-org',
  checkOrgAvailability: ORG_AVAILABILITY_ROUTE,
  patchOnboardingState: 'PATCH /onboarding/state',
  patchBillingPlan: 'PATCH /billing-plan',
  cancelSubscription: 'POST /subscription-cancel',
  stripeSubscriptionWebhook: 'POST /webhooks/stripe-subscription',
} as const satisfies Record<string, AccountsRouteKey>;
export type AccountsProcedureName = keyof typeof accountsProcedureRoutes;

/**
 * Runtime contract consumed by the future `org.accounts.*` router. The explicit
 * route keys prevent a partial/sample extraction: all 46 routes must be present
 * in both the input and output registries for this module to typecheck.
 */
type AccountsProcedureSchemaMap = {
  [P in AccountsProcedureName]: {
    input: (typeof accountsRouteInputSchemas)[(typeof accountsProcedureRoutes)[P]];
    output: (typeof accountsRouteOutputSchemas)[(typeof accountsProcedureRoutes)[P]];
  };
};

export const accountsProcedureSchemas = Object.fromEntries(
  (Object.keys(accountsProcedureRoutes) as AccountsProcedureName[]).map((procedure) => {
    const route = accountsProcedureRoutes[procedure];
    return [
      procedure,
      {
        input: accountsRouteInputSchemas[route],
        output: accountsRouteOutputSchemas[route],
      },
    ];
  }),
) as AccountsProcedureSchemaMap;

export type AccountsRouteInput<R extends AccountsRouteKey> = z.input<
  (typeof accountsRouteInputSchemas)[R]
>;
export type AccountsRouteOutput<R extends AccountsRouteKey> = z.output<
  (typeof accountsRouteOutputSchemas)[R]
>;
